import React from 'react';
import { GapSizeType } from '../../../../theme/type/gap-type';
import { Tooltip } from '../../tooltip/tooltip';
import { cn, marginBottomClass } from '../../../../utils/tailwind';

type TabsMenuItemsInterface<T> = {
    label: string | React.ReactNode;
    value: T;
    badge?: number | string | null;
    disabled?: boolean;
    tooltip?: string;
};

interface TabsMenuPropsInterface<T> {
    items: Array<TabsMenuItemsInterface<T>>;
    variant?: 'default' | 'cards';
    value: T;
    marginBottom?: GapSizeType;
    disableGradient?: boolean;
    disableBottomPadding?: boolean;
    onChange: ((newValue: T) => Promise<void>) | ((newValue: T) => void);
}

export const TabsMenu = <T,>({
    items,
    value,
    onChange,
    variant = 'default',
    marginBottom = 'vlarge',
    disableGradient,
    disableBottomPadding = false,
}: TabsMenuPropsInterface<T>) => {
    const isCards = variant === 'cards';

    return (
        <div
            className={cn(
                'flex items-center',
                isCards && 'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                !isCards && !disableBottomPadding && 'pb-vsmall',
                isCards &&
                    cn(
                        'relative pt-[1rem]',
                        marginBottomClass[marginBottom],
                        "[--container-width:min(100%,theme(maxWidth.container))] [--side-space:calc((100vw-var(--container-width))/2)] before:absolute before:bottom-0 before:right-full before:h-[0.1rem] before:w-[var(--side-space)] before:bg-border before:content-[''] after:absolute after:bottom-0 after:left-full after:h-[0.1rem] after:w-[var(--side-space)] after:bg-border after:content-['']",
                    ),
            )}
        >
            <div className={cn('inline-flex w-full items-center', isCards && 'border-b border-border')}>
                {items.map((item, index) => {
                    const isActive = item.value === value;
                    const tab = (
                        <div
                            key={index}
                            className={cn(
                                'relative whitespace-nowrap text-center transition-colors max-sm:text-xsmall max-sm:leading-xsmall',
                                isActive ? 'font-bold text-text' : 'font-medium text-text-muted',
                                item.disabled ? 'cursor-default opacity-40' : 'cursor-pointer hover:text-text',
                                isActive &&
                                    "before:absolute before:bottom-[-0.1rem] before:left-0 before:right-0 before:z-[100] before:h-[0.1rem] before:bg-body before:content-[''] after:absolute after:bottom-[-0.2rem] after:left-1/2 after:z-[101] after:h-[0.2rem] after:w-[1.6rem] after:-translate-x-1/2 after:translate-y-[0.5rem] after:[background-image:var(--gradient-primary)] after:content-['']",
                                !isCards && 'ml-small first:ml-0',
                                isCards &&
                                    cn(
                                        'mb-[-0.1rem] rounded-t-large border border-body border-b-border px-xsmall py-xsmall sm:px-medium sm:py-vsmall',
                                        isActive &&
                                            cn(
                                                'border-border border-b-body font-black',
                                                !disableGradient &&
                                                    'bg-[linear-gradient(0deg,rgb(var(--color-bg-body-rgb))_0%,rgba(var(--color-gray-rgb)/0.16)_100%)]',
                                                'after:!bottom-[0.2rem] after:!translate-y-0',
                                            ),
                                    ),
                            )}
                            onClick={item.disabled ? undefined : () => onChange(item.value)}
                        >
                            {item.label}
                        </div>
                    );

                    if (item.disabled && item.tooltip) {
                        return (
                            <Tooltip key={index} content={item.tooltip} position="top">
                                {tab}
                            </Tooltip>
                        );
                    }

                    return tab;
                })}
            </div>
        </div>
    );
};
